/**
 * A message that ARRIVED in an agent's terminal, as opposed to one the owner typed.
 *
 * Both kinds reach the pty as plain text and land in the transcript as `user`
 * blocks — the same block kind the owner's own keystrokes produce — so Clean View
 * drew them with the owner's avatar and the word "you". The owner's report: 我不要
 * 每次都看到我自己的頭像在那邊用 Agent 的標籤講話. It is not a cosmetic complaint:
 * the transcript was attributing another agent's words to them.
 *
 * The two framings, verified by reading real blocks off /agent/transcript rather
 * than from the server source (wtserver/agent_api.py `_frame_incoming`,
 * wtserver/project.py):
 *
 *   <agent-msg:HASH>            <project-msg:PROJECT_ID from=LABEL>
 *   …body…                      …body…
 *   </agent-msg:HASH>           </project-msg:PROJECT_ID>
 *                               ↳ Project message from LABEL. You are attached as …
 *   ↳ Agent message from LABEL (channel HASH) (you are channel MINE).
 *     How the agent API works — …
 *     To reply:  grep -l …
 *
 * And the third, which Clean View could not read until 2026-08-22 — owner: 讓聊天室
 * 裡面發送對話的 Agent 的身分或是 human 的身分可以被 Agent 的 clean view 給解析.
 * A cloud conversation reaching the pane (wtserver/chatFeed.ts `frameChat`):
 *
 *   <chat-msg:CONV16 from=Y Tai>                       ← a person typed it
 *   <chat-msg:CONV16 from=Y Tai via-agent=46045aa8426c> ← an agent said it, for them
 *   …body…
 *   </chat-msg:CONV16>
 *   ↳ Chat message (conversation CONV16, seq N). …
 *
 * Unparsed, every one of these rendered under the OWNER's avatar with the word
 * "you" and the raw opening tag as its first line — the same misattribution the
 * other two framings were fixed for, on the lane that carries other PEOPLE's words.
 *
 * `via-agent` carries a HASH and no name, which is deliberate on the wire: the
 * hash is the identity that never changes, and the roster on this side already
 * turns it into a name and a face. The `from=` half stays the human the agent
 * spoke FOR — chat's own rule (chatv1 `via_agent`: "the owner's key is `sender`,
 * but it speaks with its own identity"), so the two are kept apart here rather
 * than one being dropped.
 *
 * Everything after the closing tag is instructions the server writes for the
 * RECEIVING agent — how to reply, where the README is. It is not part of what the
 * sender said and it repeats verbatim on every message, so it is stripped here for
 * the same reason the server strips it from its own previews: kept, it would spend
 * the whole bubble before reaching a word anyone wrote.
 */

export type InboundKind = 'agent' | 'project' | 'chat' | 'friend'

export type InboundMessage = {
  kind: InboundKind
  /**
   * The sender's channel hash, or null.
   *
   * Present for `agent-msg`, where it is the tag itself. NEVER invented for
   * `project-msg`, which carries only a label — this seeds the PixelPet, and
   * seeding on the label instead would draw a DIFFERENT face for the same agent
   * than the rail and the Activity feed draw. Callers resolve the label against
   * the roster; a face they cannot resolve is one they must not guess.
   */
  hash: string | null
  /** Readable sender: an agent's session (possibly `session@host`), or an email. */
  label: string
  /** The room this arrived in: a project id, or a chat conversation id. */
  room?: string
  /**
   * The human an agent spoke FOR — `chat` only, and only when the frame names
   * both. Kept separate from `label` rather than folded into it: the speaker is
   * the agent (that is whose face and name belong on the row) and this is the
   * account it speaks under. Collapsing them would put a person's name on an
   * agent's message, which is the misattribution this file exists to end.
   */
  spokenFor?: string
  /**
   * Files carried by a chat message, when its body is a file card rather than
   * prose. Absent for an ordinary message — never an empty array, so a caller can
   * ask `if (files)` without also asking `.length`.
   *
   * CARRIES `file_id`, WHICH IS WHAT MAKES THE PICTURE POSSIBLE. This used to keep
   * only the name and size, so Clean View could say a file had arrived and never
   * show it — owner, 2026-08-22: 目前 Clean View 無法解析 Chat 來的 image. The id is
   * already in the card the composer sent; dropping it was the whole gap. On the
   * DynamoDB lane `dynamoAttachmentBytes` needs the id ALONE (it ignores the
   * conversation), so nothing else has to be looked up.
   */
  files?: { name: string; file_id?: string; mime?: string; size?: number; thumb?: string | null }[]
  /**
   * A FRIEND's address — `friend` only, read from the trailer the cloud writes under their
   * words (agent_shares.py `framed`). It is how the friend's own view knows the message is
   * theirs: a display name can be anyone's, the address is the account's.
   */
  email?: string
  /** What the sender actually wrote. */
  body: string
}

/** `<agent-msg:HASH>` … `</agent-msg:HASH>` — the tag is a bare hash by design. */
const AGENT_OPEN = /^<agent-msg:([^>\s]+)>\n?/
/** The ↳ trailer names the sender: "from avengents-app-builder@yy-mbp-home (channel …)". */
const AGENT_FROM = /^↳ Agent message from (.+?) \(channel /m
/** A friend link's trailer (agent sharing, phase 2): "↳ From helper, an agent of Bob — …". */
const LINK_FROM = /^↳ From (.+?), an agent of (.+?) — /m
const PROJECT_OPEN = /^<project-msg:([^>\s]+) from=([^>]*)>\n?/
/**
 * `<chat-msg:CONV from=NAME[ via-agent=HASH]>`.
 *
 * Everything between `from=` and `>` is taken as one field and split afterwards,
 * because a display name contains spaces — "Y Tai", "audionyx", "Rachel" are all
 * real — so a whitespace-delimited pattern would read the surname as the next
 * attribute. Verified against real blocks in the transcripts on this machine, not
 * from the server source.
 */
const CHAT_OPEN = /^<chat-msg:([^>\s]+) from=([^>]*)>\n?/
const VIA = ' via-agent='
/**
 * `<friend-msg from="NAME" email="EMAIL">` … `</friend-msg>` — a person the owner SHARED this agent
 * with, speaking under their own name (owner, 2026-10-17: 以好友的名稱進入 Session 發言). The cloud
 * builds the frame: it makes any tag inside the friend's words inert (so the first `</friend-msg>`
 * is the frame's own), strips `"` and `<>` from the name, and writes the ADDRESS as its own
 * attribute. The address is never read out of the trailer's prose: a name like
 * `M (carol@x), a friend` passed as Carol there (review of 2026-10-17).
 */
const FRIEND_OPEN = /^<friend-msg from="([^"]*)"(?: email="([^"]*)")?>\n?/

/**
 * A chat body that is a FILE CARD rather than prose:
 *
 *   {"type":"file","text":"…caption…","attachments":[{"file_id":…,"name":"IMG.png",…}]}
 *
 * The composer sends that JSON as the message body, so an unparsed chat row showed
 * the reader a line of JSON where the caption should be. Returns null for ordinary
 * text — including text that merely starts with a brace, since JSON.parse decides,
 * not a guess about the first character.
 */
function fileCard(body: string): { text: string; files: NonNullable<InboundMessage['files']> } | null {
  if (!body.startsWith('{')) return null
  let card: unknown
  try {
    card = JSON.parse(body)
  } catch {
    return null
  }
  if (!card || typeof card !== 'object') return null
  const c = card as { type?: unknown; text?: unknown; attachments?: unknown }
  if (c.type !== 'file') return null
  const list = Array.isArray(c.attachments) ? c.attachments : []
  type Raw = { name?: unknown; size?: unknown; file_id?: unknown; mime?: unknown; thumb?: unknown }
  const files = list
    .map((a) => (a && typeof a === 'object' ? (a as Raw) : null))
    .filter((a): a is Raw => a !== null)
    .map((a) => ({
      // A card with no usable name still says a file came — silence would read as
      // "the caption was the whole message".
      name: typeof a.name === 'string' && a.name ? a.name : 'file',
      // Everything below is OPTIONAL on purpose: an older card, or one from a lane
      // that never carried an id, still renders as the named chip it always did.
      ...(typeof a.file_id === 'string' && a.file_id ? { file_id: a.file_id } : {}),
      ...(typeof a.mime === 'string' && a.mime ? { mime: a.mime } : {}),
      ...(typeof a.size === 'number' ? { size: a.size } : {}),
      ...(typeof a.thumb === 'string' && a.thumb ? { thumb: a.thumb } : {}),
    }))
  return { text: typeof c.text === 'string' ? c.text : '', files }
}

/**
 * Read one transcript block as an arrived message, or null if the owner typed it.
 *
 * Deliberately anchored at the START of the block. A message whose BODY quotes the
 * framing — which happens constantly here, because these agents discuss the
 * protocol — must not be mistaken for the framing itself, and an unanchored search
 * would reattribute the owner's own message to whatever hash they had pasted into
 * it.
 */
export function parseInbound(text: string | undefined | null): InboundMessage | null {
  const raw = text ?? ''

  const agent = AGENT_OPEN.exec(raw)
  if (agent?.[1]) {
    const hash = agent[1]
    const rest = raw.slice(agent[0].length)
    const close = rest.indexOf(`</agent-msg:${hash}>`)
    const body = close === -1 ? rest : rest.slice(0, close)
    const trailer = close === -1 ? '' : rest.slice(close)
    const named = AGENT_FROM.exec(trailer)
    // A friend's agent, across accounts: its name, and whose it is — the cloud wrote both.
    const linked = LINK_FROM.exec(trailer)
    return {
      kind: 'agent',
      hash,
      // The trailer's name when the edge could resolve it, else the hash — which is
      // the server's own fallback, and it says "from somewhere this machine does not
      // know" rather than claiming a name.
      label: linked ? `${linked[1]!.trim()} (${linked[2]!.trim()}’s)` : named?.[1]?.trim() || hash,
      body: body.trim(),
    }
  }

  const chat = CHAT_OPEN.exec(raw)
  if (chat?.[1]) {
    const conv = chat[1]
    const attrs = (chat[2] ?? '').trim()
    // lastIndexOf: the LAST ` via-agent=` is the attribute; an earlier one would
    // have to be inside a person's display name, and the attribute is written last.
    const at = attrs.lastIndexOf(VIA)
    const via = at === -1 ? '' : attrs.slice(at + VIA.length).trim()
    const human = (at === -1 ? attrs : attrs.slice(0, at)).trim()
    const rest = raw.slice(chat[0].length)
    const close = rest.indexOf(`</chat-msg:${conv}>`)
    const said = (close === -1 ? rest : rest.slice(0, close)).trim()
    const card = fileCard(said)
    return {
      kind: 'chat',
      // The agent's channel hash when one spoke; null when a person typed it, and
      // never the person — a human has no hash, and inventing one would seed them
      // a generated creature for a face.
      hash: via || null,
      // The speaker. An agent is named by its hash until the roster resolves it,
      // exactly as the agent-msg path falls back; a person is named outright.
      label: via || human || 'someone',
      ...(via && human ? { spokenFor: human } : {}),
      room: conv,
      ...(card && card.files.length ? { files: card.files } : {}),
      body: card ? card.text : said,
    }
  }

  const friend = FRIEND_OPEN.exec(raw)
  if (friend) {
    const rest = raw.slice(friend[0].length)
    const close = rest.indexOf('</friend-msg>')
    const email = friend[2] || undefined
    return {
      kind: 'friend',
      // A person: no hash, and none invented (see `hash`).
      hash: null,
      label: (friend[1] ?? '').trim() || email || 'a friend',
      ...(email ? { email } : {}),
      body: (close === -1 ? rest : rest.slice(0, close)).trim(),
    }
  }

  const project = PROJECT_OPEN.exec(raw)
  if (project?.[1]) {
    const room = project[1]
    const rest = raw.slice(project[0].length)
    const close = rest.indexOf(`</project-msg:${room}>`)
    return {
      kind: 'project',
      // No hash on the wire, and none is fabricated here. See the field's comment.
      hash: null,
      label: (project[2] ?? '').trim() || 'someone',
      room,
      body: (close === -1 ? rest : rest.slice(0, close)).trim(),
    }
  }

  return null
}

/**
 * Resolve a framing's readable label back to a roster session, so its avatar is the
 * SAME face that agent wears everywhere else.
 *
 * Labels arrive in two shapes — `session` for an agent on this edge and
 * `session@host` for one reached over a mount — and the host half is not part of
 * the identity the pet is seeded from. An email never matches, which is correct:
 * a human has no channel hash, and the caller draws them a plain tile instead of a
 * generated creature.
 */
export function resolveSender(
  label: string,
  roster: { session: string; host?: string | null; hash?: string }[],
): string | null {
  const want = label.trim()
  if (!want || want.includes('@') === false) {
    const exact = roster.find((a) => a.session === want)
    if (exact?.hash) return exact.hash
  }
  const [session, host] = want.split('@')
  const byPair = roster.find(
    (a) => a.session === session && (a.host ?? '') === (host ?? ''),
  )
  if (byPair?.hash) return byPair.hash
  // An email — "a@b.com" splits to session "a" — must not match an agent named "a".
  if (want.includes('.') && want.includes('@')) return null
  const bySession = roster.find((a) => a.session === session)
  return bySession?.hash ?? null
}
